import React from "react";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import OrderStatus from "@/enum/OrderStatus";
import OrderDTO from "@/dto/order/OrderDTO";
import ShopType from "@/enum/ShopType";
import StringUtils from "./StringUtils";
import LogisticsCompany from "@/enum/LogisticsCompany";
import OrderItemDTO from "@/dto/order/OrderItemDTO";
import OrderConsigneeDTO from "@/dto/order/OrderConsigneeDTO";
import { groupBy, memoize, uniq } from "lodash";
import { UndeliverableAreaCode } from "@/services/undeliverableAreaService";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import OrderTag from "@/enum/OrderTag";
import CombineItemInfoDTO from "@/dto/order/CombineItemInfoDTO";
import ExtraInfoDTO from "@/dto/order/ExtraInfoDTO";
import OrderSkuQuantity from "@/enum/OrderSkuQuantity";
import GlobalStore from "@/store";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";
import { Modal, Tag, Tooltip } from "antd";
import { isChildOrderShouldBeSelected } from "./OrderSelectUtil";
import EwaybillDTO from "@/dto/order/EwaybillDTO";
import SellerRemarkFlagIcon from "@/components/SellerRemarkFlagIcon";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import Big from "big.js";

/**
 * 校验（发货前校验），如果不符合规则显示错误
 * 规则：
 * 1. 物流公司id不了为空
 * 2. 运单号不了为空
 * 3. 运单号必须符合物流公司要求
 * @return {String} 校验通过返回null，否则error string
 */
function shipValidate(shopType: ShopType, waybillNo?: string, logisticsCompany?: LogisticsCompany) {
    // 处于激活状态后，用户操作会触发重新校验，目的是用于在用户输入正确的运单号后隐藏错误信息
    const errors = [];

    if (!logisticsCompany) {
        errors.push("没有物流公司");
    }

    if (!waybillNo) {
        if (shopType === ShopType.OTHER_DROP_SHIPPING_SUPPLIER) {
            errors.push("代发订单必须使用运单号发货");
        } else {
            errors.push("没有运单号");
        }
    }

    if (errors.length) {
        return errors.join(",");
    }

    return null;
}

function isMergerOrder(order: OrderDTO) {
    return order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT;
}

export function isBlacklistOrder(order: OrderDTO) {
    return order.tags?.includes(OrderTag.BUYER_BLACKLIST);
}

function isHistoryOrder(order: OrderDTO) {
    return !!order.isHistoryOrder;
}

function buildOrderDetailsUrl(shopType: ShopType, originalId: string) {
    if (shopType === ShopType.TAOBAO) {
        return `https://trade.taobao.com/trade/detail/trade_item_detail.htm?bizOrderId=${originalId}`;
    }
    if (shopType === ShopType.ALIBABA) {
        return `https://trade.1688.com/order/order_detail.htm?order_id=${originalId}`;
    }
    if (shopType === ShopType.WEIDIAN) {
        return `https://d.weidian.com/orderNew/orderDetail/order_detail.php?orderId=${originalId}`;
    }
    if (shopType === ShopType.YOUZAN) {
        return `https://www.youzan.com/v2/trade/order/detail?order_no=${originalId}`;
    }
    if (shopType === ShopType.PINDUODUO) {
        return `https://mms.pinduoduo.com/orders/detail?sn=${originalId}`;
    }
    if (shopType === ShopType.CHUCHUJIE) {
        return `http://seller.chuchujie.com/sqe.php?s=/Order/order_info/order_sn/${originalId}`;
    }
    if (shopType === ShopType.JD) {
        return "https://neworder.shop.jd.com/order/orderDetail?orderId=" + originalId;
    }
    if (shopType === ShopType.KWAI) {
        return "https://s.kwaixiaodian.com/zone-origin/order/detail?id=" + originalId;
    }
    if (shopType === ShopType.DOUDIAN) {
        return "https://fxg.jinritemai.com/ffa/morder/order/detail-v2?id=" + originalId;
    }

    return null;
}

function buildMergerOrderBuyerRemarks(order: OrderDTO) {
    // 将所有子订单的买家留言拼接起来
    const buffer: string[] = [];
    order.childOrders.forEach((it) => {
        if (it.buyerRemarks) {
            const { localContent, content } = it.buyerRemarks;
            if (localContent) {
                if (content) {
                    buffer.push(`${localContent}(原:${content})`);
                } else {
                    buffer.push(localContent);
                }
            } else if (content) {
                buffer.push(content);
            }
        }
    });

    return buffer.length > 0 ? buffer.join("; ") : null;
}

function buildBuyerRemarks(order: Pick<OrderDTO, "buyerRemarks">) {
    // if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
    //     return buildMergerOrderBuyerRemarks(order);
    // }

    const buyerRemarks = order.buyerRemarks;
    if (!buyerRemarks) {
        return null;
    }

    const { localContent, content } = buyerRemarks;

    if (localContent) {
        if (content) {
            return `${localContent}(原:${content})`;
        }
        return localContent;
    }

    return content;
}

function buildMergerOrderSellerRemarks(order: OrderDTO) {
    const buffer: string[] = [];

    order.childOrders.forEach((it) => {
        if (it.sellerRemarks) {
            const { localContent, content } = it.sellerRemarks;
            if (localContent) {
                if (content) {
                    buffer.push(`${localContent}(原:${content})`);
                } else {
                    buffer.push(localContent);
                }
            } else if (content) {
                buffer.push(content);
            }
        }
    });

    return buffer.length > 0 ? buffer.join("; ") : null;
}

function buildSellerRemarks(order: Pick<OrderDTO, "sellerRemarks">) {
    // if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
    //     return buildMergerOrderSellerRemarks(order);
    // }

    if (!order.sellerRemarks) {
        return null;
    }

    const { localContent, content } = order.sellerRemarks;

    if (localContent) {
        return localContent;
    }

    return content;
}

export function buildFlags(order: OrderDTO) {
    const flags = [];

    let index = 0;

    if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
        order.childOrders.forEach((childOrder) => {
            const { sellerRemarks } = childOrder;

            if (sellerRemarks) {
                const { flag, localFlag, flagTag } = sellerRemarks;

                if (localFlag && flags.indexOf(localFlag) === -1) {
                    if (localFlag !== "NONE") {
                        flags.push(<SellerRemarkFlagIcon orderSellerRemarksFlag={localFlag} key={index++} />);
                    }
                } else if (flag && flags.indexOf(flag) === -1) {
                    if (flag !== "NONE") {
                        if (flagTag) {
                            flags.push(
                                <Tooltip title={flagTag} key={index++}>
                                    <SellerRemarkFlagIcon orderSellerRemarksFlag={flag} />
                                </Tooltip>,
                            );
                        } else {
                            flags.push(<SellerRemarkFlagIcon key={index++} orderSellerRemarksFlag={flag} />);
                        }
                    }
                }
            }
        });
    } else {
        const { sellerRemarks } = order;
        if (sellerRemarks) {
            const { flag, localFlag, flagTag } = sellerRemarks;

            if (localFlag && flags.indexOf(localFlag) === -1) {
                if (localFlag !== "NONE") {
                    flags.push(<SellerRemarkFlagIcon orderSellerRemarksFlag={localFlag} key={index++} />);
                }
            } else if (flag && flags.indexOf(flag) === -1) {
                if (flagTag) {
                    flags.push(
                        <Tooltip title={flagTag} key={index++}>
                            <SellerRemarkFlagIcon orderSellerRemarksFlag={flag} />
                        </Tooltip>,
                    );
                } else {
                    flags.push(<SellerRemarkFlagIcon key={index++} orderSellerRemarksFlag={flag} />);
                }
            }
        }
    }

    return flags;
}

export function buildFlagTag(order: OrderDTO) {
    if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
        let res = [];

        order.childOrders.forEach((childOrder) => {
            const { sellerRemarks } = childOrder;

            if (sellerRemarks && !sellerRemarks.localFlag && sellerRemarks.flagTag) {
                res.push(
                    <Tag
                        variant="outlined"
                        className="mr5 small"
                        style={{ padding: "0 2px", marginRight: 8 }}
                        key={res.length}
                    >
                        {sellerRemarks.flagTag}
                    </Tag>,
                );
            }
        });

        return res;
    } else {
        const { sellerRemarks } = order;

        if (sellerRemarks && !sellerRemarks.localFlag && sellerRemarks.flagTag) {
            return (
                <Tag variant="outlined" className="mr5 small" style={{ padding: "0 2px" }}>
                    {sellerRemarks.flagTag}
                </Tag>
            );
        }
    }
}

export function getRealOrderOriginalId(order: OrderDTO) {
    const isMergerOrder = order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT;
    const isSplitChildOrder = order.mergeSplitStatus === OrderMergeSplitStatus.SPLIT_CHILD;

    let orderOriginalId = order.originalId;

    if (isMergerOrder && order?.childOrders?.length) {
        orderOriginalId = order.originalId.replace("M", "");
    }

    if (isSplitChildOrder) {
        const index = order.originalId.lastIndexOf("-");

        if (index > -1) {
            orderOriginalId = order.originalId.substring(0, index);
        }
    }

    return orderOriginalId;
}

function getOriginalIds(order: OrderDTO) {
    if (!order) return [];

    const originalIds: string[] = [];

    if (order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders) {
        order.childOrders.forEach((it) => {
            if (it.originalId) {
                originalIds.push(it.originalId);
            }
        });
    } else {
        originalIds.push(order.originalId);
    }

    return originalIds;
}

/**
 * 如果订单有本地收货人信息优先使用本地收货人信息
 * @param order
 * @returns {boolean}
 */
function hasLocalConsigneeInfo(consignee: OrderConsigneeDTO) {
    if (!consignee) return false;
    return consignee.localProvince && consignee.localCity && consignee.localStreet;
}

/**
 * 简化省名
 */
function simplifyProvince(province: string) {
    if (!province) {
        return "";
    }
    if (province.indexOf("黑龙江") > -1) {
        return "黑龙江";
    }
    if (province.indexOf("内蒙古") > -1) {
        return "内蒙古";
    }

    return province.substring(0, 2);
}

function getConsigneeName(order: Pick<OrderDTO, "consignee">) {
    const { consignee } = order;
    return (hasLocalConsigneeInfo(consignee) ? consignee.localName : consignee.name) || "";
}

function getConsigneeMobile(order: Pick<OrderDTO, "consignee">) {
    const { consignee } = order;
    return (hasLocalConsigneeInfo(consignee) ? consignee.localMobile : consignee.mobile) || "";
}

function getConsigneeTelephone(order: Pick<OrderDTO, "consignee">) {
    const { consignee } = order;
    return (hasLocalConsigneeInfo(consignee) ? consignee.localTelephone : consignee.telephone) || "";
}

function getConsigneeZipCode(order: Pick<OrderDTO, "consignee">) {
    const { consignee } = order;
    return (hasLocalConsigneeInfo(consignee) ? consignee.localZipCode : consignee.zipCode) || "";
}

function getConsigneeProvince(order: Pick<OrderDTO, "consignee">) {
    const { consignee } = order;
    const province = hasLocalConsigneeInfo(consignee) ? consignee.localProvince : consignee.province;
    return simplifyProvince(province);
}

function getConsigneeCity(order: Pick<OrderDTO, "consignee">) {
    const { consignee } = order;
    return (hasLocalConsigneeInfo(consignee) ? consignee.localCity : consignee.city) || "";
}

function getConsigneeDistrict(order: Pick<OrderDTO, "consignee">) {
    const { consignee } = order;
    return (hasLocalConsigneeInfo(consignee) ? consignee.localDistrict : consignee.district) || "";
}

function getConsigneeStreet(order: Pick<OrderDTO, "consignee">) {
    const { consignee } = order;
    if (hasLocalConsigneeInfo(consignee)) {
        return consignee.localStreet;
    }

    const town = consignee.town;
    const street = consignee.street;

    if (street && town && street.indexOf(town) !== 0) {
        return town + street;
    }

    return street;
}

function getConsigneeAddress(order: Pick<OrderDTO, "consignee">) {
    const { consignee } = order;
    const hasLocalConsignee = hasLocalConsigneeInfo(consignee);

    if (hasLocalConsignee) {
        const province = simplifyProvince(consignee.localProvince);
        return StringUtils.join(province, consignee.localCity, consignee.localDistrict, consignee.localStreet);
    }

    const province = simplifyProvince(consignee.province);
    const city = consignee.city;
    const district = consignee.district;
    const street = consignee.street;
    const town = consignee.town;

    if (town && street && street.startsWith(town)) {
        return StringUtils.join(province, city, district, street);
    }
    return StringUtils.join(province, city, district, town, street);
}

function getOriginalConsigneeAddress(order: Pick<OrderDTO, "consignee">) {
    const { consignee } = order;
    const province = simplifyProvince(consignee.province);
    const city = consignee.city;
    const district = consignee.district;
    const street = consignee.street;
    const town = consignee.town;

    if (town && street && street.startsWith(town)) {
        return StringUtils.join(province, city, district, street);
    }
    return StringUtils.join(province, city, district, town, street);
}

export function isConsigneeExist(consignee: OrderConsigneeDTO) {
    if (hasLocalConsigneeInfo(consignee)) return true;

    return !!(
        consignee.province ||
        consignee.city ||
        consignee.district ||
        consignee.street ||
        consignee.town ||
        consignee.name ||
        consignee.telephone ||
        consignee.mobile ||
        consignee.zipCode
    );
}

function getConsigneeInformation(order: OrderDTO) {
    const address = getConsigneeAddress(order);
    const name = getConsigneeName(order);
    const phone = getConsigneeMobile(order);
    const telePhone = getConsigneeTelephone(order);
    const zipCode = getConsigneeZipCode(order);

    const consigneeInformation = [];

    if (name) {
        consigneeInformation.push(`${name}，`);
    }

    if (phone || telePhone) {
        consigneeInformation.push(`${phone ? phone : ""}${telePhone ? telePhone : ""}，`);
    }

    if (address) {
        consigneeInformation.push(`${address}`);

        if (zipCode) {
            consigneeInformation.push(`，${zipCode}`);
        }
    }

    return consigneeInformation.join("");
}

function chartCodeComparator(str1: string, str2: string) {
    str1 = str1?.trim?.() || "";
    str2 = str2?.trim?.() || "";
    const length1 = str1.length;
    const length2 = str2.length;
    const length = length1 > length2 ? length2 : length1;
    for (let i = 0; i < length; i++) {
        const charCode1 = str1.charCodeAt(i);
        const charCode2 = str2.charCodeAt(i);
        if (charCode1 !== charCode2) {
            return charCode1 - charCode2;
        }
    }

    return length1 - length2;
}

function doSortOrderItemsBySkuProperties(item1: OrderItemDTO, item2: OrderItemDTO) {
    const id1 = item1.skuProperties || "";
    const id2 = item2.skuProperties || "";

    return chartCodeComparator(id1, id2);
}

function doSortOrderItemsByTitle(item1: OrderItemDTO, item2: OrderItemDTO) {
    const title1 = item1.title || "";
    const title2 = item2.title || "";

    const i = chartCodeComparator(title1, title2);

    if (i !== 0) {
        return i;
    }

    // 标题一致则按照属性来排序
    return doSortOrderItemsBySkuProperties(item1, item2);
}

function doSortOrderItemsByExternalId(item1: OrderItemDTO, item2: OrderItemDTO) {
    const externalId1 = item1.skuExternalId || item1.productExternalId || "";
    const externalId2 = item2.skuExternalId || item2.productExternalId || "";

    const i = chartCodeComparator(externalId1, externalId2);

    if (i !== 0) {
        return i;
    }

    // 商家编码一致则按照属性来排序
    return doSortOrderItemsBySkuProperties(item1, item2);
}

// 根据商品货位—商品编码—商品属性顺序来排序
function sortOrderItemsByStockNo(orderItems: OrderItemDTO[]) {
    if (!orderItems || orderItems.length <= 1) {
        return orderItems;
    }

    // 克隆orderItem是为了避免排序改变在订单详情窗口中的顺序
    const clonedOrderItems = orderItems.slice(0);

    // 赠品放在列表最后
    const giftItems: OrderItemDTO[] = [];

    // 如果有货位则参加排序，没有货位则按照商品编码排序，如果货位和商品编码都没有则按原顺序放到最后
    const sortByStockNoItems: OrderItemDTO[] = [];
    const sortByExternalIdItems: OrderItemDTO[] = [];
    const notSortableItems: OrderItemDTO[] = [];

    clonedOrderItems.forEach((item) => {
        if (item.isGift) {
            giftItems.push(item);
            return;
        }

        if (item.stockNo) {
            sortByStockNoItems.push(item);
            return;
        }

        if (item.skuExternalId || item.productExternalId) {
            sortByExternalIdItems.push(item);
            return;
        }

        notSortableItems.push(item);
    });

    sortByStockNoItems.sort((item1, item2) => chartCodeComparator(item1.stockNo, item2.stockNo));

    sortByExternalIdItems.sort((item1, item2) => doSortOrderItemsByExternalId(item1, item2));

    return sortByStockNoItems.concat(sortByExternalIdItems, notSortableItems, giftItems);
}

// 根据商品编码排序
function sortOrderItemsByExternalId(orderItems: OrderItemDTO[]) {
    if (!orderItems || orderItems.length <= 1) {
        return orderItems;
    }

    // 克隆orderItem是为了避免排序改变在订单详情窗口中的顺序
    const clonedOrderItems = orderItems.slice(0);

    // 赠品放在列表最后
    const giftItems: OrderItemDTO[] = [];

    // 如果有商品编码则参加排序，没有商品编码则按标题进行排序，也没有标题则按照原顺序
    const sortByExternalIdItems: OrderItemDTO[] = [];
    const sortByTitleItems: OrderItemDTO[] = [];
    const notSortableItems: OrderItemDTO[] = [];

    clonedOrderItems.forEach((item) => {
        if (item.isGift) {
            giftItems.push(item);
            return;
        }

        if (item.skuExternalId || item.productExternalId) {
            sortByExternalIdItems.push(item);
        } else if (item.title) {
            sortByTitleItems.push(item);
        } else {
            notSortableItems.push(item);
        }
    });

    sortByExternalIdItems.sort((item1, item2) => doSortOrderItemsByExternalId(item1, item2));

    sortByTitleItems.sort((item1, item2) => doSortOrderItemsByTitle(item1, item2));

    return sortByExternalIdItems.concat(sortByTitleItems, notSortableItems, giftItems);
}

// 根据商品编码排序
function sortOrderItemsByTitle(orderItems: OrderItemDTO[]) {
    if (!orderItems || orderItems.length <= 1) {
        return orderItems;
    }

    // 克隆orderItem是为了避免排序改变在订单详情窗口中的顺序
    const clonedOrderItems = orderItems.slice(0);

    // 赠品放在列表最后
    const giftItems: OrderItemDTO[] = [];

    const sortByTitleItems: OrderItemDTO[] = [];
    const notSortableItems: OrderItemDTO[] = [];

    clonedOrderItems.forEach((item) => {
        if (item.isGift) {
            giftItems.push(item);
            return;
        }

        if (item.title) {
            sortByTitleItems.push(item);
        } else {
            notSortableItems.push(item);
        }
    });

    sortByTitleItems.sort((item1, item2) => doSortOrderItemsByTitle(item1, item2));

    return sortByTitleItems.concat(notSortableItems, giftItems);
}

function isAnyOrderItemSelected(order: OrderDTO) {
    const orderItems = order.orderItems;
    for (let i = 0; i < orderItems.length; i++) {
        const orderItem = orderItems[i];
        // @ts-ignore
        if (orderItem.isSelected) {
            return true;
        }
    }
    return false;
}

function isOrderItemShouldBeSelected(order: OrderDTO, orderItem: OrderItemDTO) {
    // 该逻辑与OrderRow.select()一致，如果需要需要统一修改，以后引进Store后将该逻辑统一到Model中
    const selectable = orderItem.status !== OrderStatus.CANCELED || order.status === OrderStatus.CANCELED;

    // 已部分发货
    const isPartShipped =
        order.status === OrderStatus.PENDING_SHIPMENT && orderItem.status !== OrderStatus.PENDING_SHIPMENT;

    return selectable && !isPartShipped;
}

function selectAllAvailableOrderItem(order: OrderDTO) {
    if (isMergeOrder(order)) {
        order.childOrders.map((childOrder) => {
            childOrder.orderItems.map((childOrderItem) => {
                // @ts-ignore
                childOrderItem.isSelected = isOrderItemShouldBeSelected(order, childOrderItem);
            });

            childOrder.isSelected = isChildOrderShouldBeSelected(childOrder);
        });
    }

    order.orderItems.map((orderItem) => {
        //@ts-ignore
        orderItem.isSelected = isOrderItemShouldBeSelected(order, orderItem);
    });
}

const CONSOLIDATION_SUFFIX = "集运";

/**
 * 街道命中过滤词，且过滤词后面紧接「集运」（如「新疆集运仓」）时，视为正常地址。
 */
function isAbnormalKeywordInStreet(street: string | undefined, keyword: string) {
    if (!street || !keyword) {
        return false;
    }

    if (street.includes(keyword + CONSOLIDATION_SUFFIX)) {
        return false;
    }

    return street.includes(keyword);
}

function _isAbnormalAddress(order: OrderDTO, addressFilterKeywords: string[]): boolean {
    if (!addressFilterKeywords.length) {
        return false;
    }

    if (order.status == OrderStatus.PENDING_SHIPMENT) {
        if (!order.consignee) return false;

        const hasLocalConsigneeInfoFlag = hasLocalConsigneeInfo(order.consignee);

        const province = hasLocalConsigneeInfoFlag ? order.consignee.localProvince : order.consignee.province;
        const city = hasLocalConsigneeInfoFlag ? order.consignee.localCity : order.consignee.city;
        const street = hasLocalConsigneeInfoFlag ? order.consignee.localStreet : order.consignee.street;

        // 特殊地址名处理
        // 如：青海省海西蒙古族藏族自治州格尔木市西藏街道盐桥南路
        if (street?.includes("西藏") && province?.includes("青海") && city?.includes("海西蒙古")) {
            return false;
        }

        return addressFilterKeywords?.some((keyword) => {
            return !province?.includes(keyword) && isAbnormalKeywordInStreet(street, keyword);
        });
    }

    return false;
}

/**
 * 判断是否异常地址
 */
const isAbnormalAddress = memoize(_isAbnormalAddress, (order, addressFilterKeywords) => {
    const { id, status } = order || {};
    const { province, city, street, localProvince, localCity, localStreet } = order?.consignee || {};

    return [id, status, province, city, street, localProvince, localCity, localStreet, ...addressFilterKeywords].join(
        ",",
    );
});

/**
 * 检查订单是否包含在不可达列表内
 */
function checkUndeliverable(order: OrderDTO, undeliverableAreaCodes: UndeliverableAreaCode[]) {
    if (!undeliverableAreaCodes?.length) {
        return null;
    }

    if (order.status == OrderStatus.PENDING_SHIPMENT) {
        let undeliverableReasons: string[] = [];

        for (let i = 0; i < undeliverableAreaCodes.length; i++) {
            const undeliverableAreaCode = undeliverableAreaCodes[i];

            const undeliverableReason = undeliverableAreaCode.checkUndeliverable(order);

            if (undeliverableReason) {
                undeliverableReasons.push(undeliverableReason);
            }
        }

        if (undeliverableReasons.length) {
            return undeliverableReasons.join("；");
        }
    }

    return null;
}

/**
 * Sku是否为定制商品
 */
export const isCustomizedSku = (
    orderItem: OrderItemDTO,
    customizationProductOriginalIds: string[],
    customizationSkuOriginalIds: string[],
) =>
    customizationProductOriginalIds.includes(orderItem.productOriginalId) ||
    customizationSkuOriginalIds.includes(orderItem.skuOriginalId);

export function isMergeOrder(order: OrderDTO) {
    return order.mergeSplitStatus === OrderMergeSplitStatus.MERGE_PARENT && order.childOrders;
}

export function isAllOrderItemSelected(order: OrderDTO) {
    if (isMergeOrder(order)) {
        return order.childOrders.every((childOrder) => {
            //@ts-ignore
            return childOrder.orderItems.every((orderItem) => orderItem.isSelected);
        });
    }

    //@ts-ignore
    return order.orderItems.every((orderItem) => orderItem.isSelected);
}

export function getOrderPendingShipmentOrderItem(order: OrderDTO) {
    const pendingShipOrderItems: OrderItemDTO[] = [];

    if (isMergeOrder(order)) {
        order.childOrders.forEach((childOrder) => {
            childOrder.orderItems.forEach((childOrderOrderItem) => {
                if (childOrderOrderItem.status === OrderStatus.PENDING_SHIPMENT)
                    pendingShipOrderItems.push(childOrderOrderItem);
            });
        });
    } else {
        pendingShipOrderItems.push(
            ...order.orderItems.filter((orderItem) => orderItem.status === OrderStatus.PENDING_SHIPMENT),
        );
    }

    return pendingShipOrderItems;
}

export function isAllPendingShipmentOrderItemSelected(order: OrderDTO) {
    const pendingShipOrderItems = getOrderPendingShipmentOrderItem(order);

    return (
        pendingShipOrderItems
            // @ts-ignore
            .every((orderItem) => orderItem.isSelected)
    );
}

export function buildShippingAddressInfo(shippingAddress: ShippingAddressDTO) {
    const { province = "", city = "", district = "", street = "", mobile = "", shipperName = "" } = shippingAddress;

    return `${shipperName} ${province}${city}${district}${street} ${mobile}`;
}

/**
 * 根据订单列表去重拿到店铺列表
 */
export function getUniqOrdersShopIds(orders: Pick<OrderDTO, "shopId">[]): number[] {
    return uniq(orders.map((order) => order.shopId));
}

/**
 * 订单列表通过shopId分组
 * 跨店铺操作基本都要此操作分组请求使用
 */
export function ordersGroupByShopId<T extends Pick<OrderDTO, "shopId">>(orders: T[]): T[][] {
    const ordersGroupByMap = groupBy(orders, "shopId");

    return Object.values(ordersGroupByMap);
}

export function hasEwaybillNo(order: OrderDTO) {
    if (order.ewaybills && order.ewaybills.length > 0) {
        return true;
    }

    if (order?.retailerDropShippingInfo?.packages?.length) {
        return true;
    }

    return false;
}

export function isManualLockOrder(order: OrderDTO) {
    return order.tags?.includes(OrderTag.LOCKED);
}

// 是否有订单退款中
export function isAnyOrderRefunding(orders: OrderDTO[]) {
    return orders.some((order) => order.isRefunding);
}

// 是否有订单已经录入运单号
export function isAnyOrderInputtedWaybillNo(orders: OrderDTO[]) {
    return orders.some((order) => !StringUtils.isBlank(order.waybillNo));
}

// 是否有订单没有录入运单号
export function isAnyOrderNotInputtedWaybillNo(orders: OrderDTO[]) {
    return orders.some((order) => StringUtils.isBlank(order.waybillNo));
}

export function parseCombineItemInfos(orderItem: OrderItemDTO) {
    let combineItemInfos: CombineItemInfoDTO[];
    try {
        if (orderItem.extraInfo) {
            const extraInfo = JSON.parse(orderItem.extraInfo) as ExtraInfoDTO;

            if (extraInfo && extraInfo.combineItemInfos) {
                combineItemInfos = extraInfo.combineItemInfos;

                /** 将待发货的放到最前面 */
                combineItemInfos.sort((prev, next) => {
                    if (prev.status === next.status) return 0;

                    if (prev.status === OrderStatus.PENDING_SHIPMENT) return -1;

                    return 1;
                });
            }
        }
    } catch (e) {
        console.error("解析CombineItemInfos出错:", e?.message);
    }

    return combineItemInfos;
}

export function parseOrderItemExtraInfo(orderItem: OrderItemDTO) {
    try {
        if (orderItem.extraInfo) {
            const extraInfo = JSON.parse(orderItem.extraInfo) as ExtraInfoDTO;

            return extraInfo;
        }
    } catch (e) {
        console.error("解析IMEI出错:", e?.message);
    }

    return undefined;
}

/**
 * 生成订单项的唯一标识Tag，用于区分不同商品项
 * 同一Tag，在前端会被视为同一种商品
 */
export function buildOrderItemTag(orderItem: OrderItemDTO) {
    const { skuOriginalId, productOriginalId, skuProperties, title, localSkuProperties, simpleTitle } = orderItem;

    return `${simpleTitle ?? title ?? ""}-${localSkuProperties ?? skuProperties ?? ""}-${productOriginalId || ""}-${
        skuOriginalId || ""
    }`;
}

/** 是否为仅获取单号不打印的订单 */
export function isGenerateWaybillNoOnlyOrder(order: OrderDTO) {
    return !order.isWaybillPrinted && order.ewaybills?.length;
}

/** 如果有部分数量发货/部分数量取消的订单，确认是否继续打印 */
export async function ensureOrderItemPartQuantityOrdersShouldPrint(
    orders: OrderDTO[],
    globalStore: typeof GlobalStore,
) {
    const partQuantityCanceledShopTypeSet = new Set<ShopType>();
    const partQuantityShippedShopTypeSet = new Set<ShopType>();
    /** 提示的序号游标 */
    let index = 1;

    for (const order of orders) {
        if (order.tags) {
            const shopType = globalStore.shopMapping.get(order.shopId)?.type;

            if (order.tags.includes(OrderTag.ORDER_ITEM_PART_QUANTITY_SHIPPED)) {
                partQuantityShippedShopTypeSet.add(shopType);
            }

            if (order.tags.includes(OrderTag.ORDER_ITEM_PART_QUANTITY_CANCELED)) {
                partQuantityCanceledShopTypeSet.add(shopType);
            }
        }
    }

    if (partQuantityCanceledShopTypeSet.size > 0 || partQuantityShippedShopTypeSet.size > 0) {
        const content = [];

        partQuantityCanceledShopTypeSet.forEach((shopType) => {
            const platform = ShopTypeNameMap[shopType];

            content.push(
                `${index++}.有${platform}订单是部分数量取消订单，打印需要核对下面单上的商品数量是否准确，避免拣错货`,
            );
        });

        partQuantityShippedShopTypeSet.forEach((shopType) => {
            const platform = ShopTypeNameMap[shopType];

            content.push(
                `${index++}.有${platform}订单是部分数量发货订单，打印需要核对下面单上的商品数量是否准确，避免拣错货`,
            );
        });

        await new Promise<boolean>((resolve, reject) => {
            Modal.confirm({
                title: "确认是否继续打印",
                onOk() {
                    resolve(true);
                },
                onCancel() {
                    reject(new Error("用户取消操作"));
                },
                focusable: { focusTriggerAfterClose: true },
                content: (
                    <div className="error">
                        {content.map((item, i) => {
                            return <p key={i}>{item}</p>;
                        })}
                    </div>
                ),
            });
        });
    }
}

/** 如果有部分数量发货/部分数量取消的订单，确认是否继续发货 */
export async function ensureOrderItemPartQuantityOrdersShouldShip(orders: OrderDTO[], globalStore: typeof GlobalStore) {
    const partQuantityCanceledShopTypeSet = new Set<ShopType>();
    const partQuantityShippedShopTypeSet = new Set<ShopType>();
    /** 提示的序号游标 */
    let index = 1;

    for (const order of orders) {
        if (order.tags) {
            const shopType = globalStore.shopMapping.get(order.shopId)?.type;

            if (order.tags.includes(OrderTag.ORDER_ITEM_PART_QUANTITY_SHIPPED)) {
                partQuantityShippedShopTypeSet.add(shopType);
            }

            if (order.tags.includes(OrderTag.ORDER_ITEM_PART_QUANTITY_CANCELED)) {
                partQuantityCanceledShopTypeSet.add(shopType);
            }
        }
    }

    if (partQuantityCanceledShopTypeSet.size > 0 || partQuantityShippedShopTypeSet.size > 0) {
        const content = [];

        partQuantityCanceledShopTypeSet.forEach((shopType) => {
            const platform = ShopTypeNameMap[shopType];

            content.push(
                `${index++}.有${platform}的订单商品部分数量已取消，将会按照真实待发货商品数量进行发货。建议在${platform}后台查看下订单的部分数量取消情况，核对下面单上的商品数量和拣货数量，避免发错货`,
            );
        });

        partQuantityShippedShopTypeSet.forEach((shopType) => {
            const platform = ShopTypeNameMap[shopType];

            content.push(
                `${index++}.有${platform}的订单商品部分数量已发货，将会按照真实待发货商品数量进行发货。建议在${platform}后台查看下订单的部分数量发货情况，核对下面单上的商品数量和拣货数量，避免发错货`,
            );
        });

        await new Promise<boolean>((resolve, reject) => {
            Modal.confirm({
                title: "确认是否继续发货",
                onOk() {
                    resolve(true);
                },
                onCancel() {
                    reject(new Error("用户取消操作"));
                },
                focusable: { focusTriggerAfterClose: true },
                content: (
                    <div className="error">
                        {content.map((item, i) => {
                            return <p key={i}>{item}</p>;
                        })}
                    </div>
                ),
            });
        });
    }
}

export async function ensureShowJdMaxEwaybillShipTip(orders: OrderDTO[], globalStore: typeof GlobalStore) {
    const showTip = orders.some((order) => {
        const shop = globalStore.shopMapping.get(order.shopId);

        return shop?.type === ShopType.JD && order.ewaybills?.length > 10;
    });

    if (showTip) {
        await new Promise((yes) => {
            Modal.info({
                title: "京东回传单号提醒",
                content: "注意！由于京东平台接口限制京东订单最多回传10个运单号到京东后台，超出的运单号无法回传",
                onOk() {
                    yes(null);
                },
            });
        });
    }
}

/**
 * 订单取消合并后是否还可以再次合并
 *
 * @https://www.tapd.cn/22185411/prong/tasks/view/1122185411001004510
 */
export function isCanReMergeOrder(shopType?: ShopType) {
    return ![ShopType.PDD_FDS, ShopType.PDD_FULFILLMENT, ShopType.DOUDIAN_IOP, ShopType.DOUDIAN_SUPPLIER].includes(
        shopType,
    );
}

export function getStatusLabelClassName(status: OrderStatus, isRefunding = false) {
    if (isRefunding) return "order-status-label label-danger";

    if (status === OrderStatus.PENDING_PAYMENT) {
        return "order-status-label label-default";
    }
    if (status === OrderStatus.PENDING_SHIPMENT) {
        return "order-status-label label-primary";
    }
    if (status === OrderStatus.SHIPPED) {
        return "order-status-label label-warning";
    }
    if (status === OrderStatus.SUCCESS) {
        return "order-status-label label-success";
    }
    if (status === OrderStatus.CANCELED) {
        return "order-status-label label-danger";
    }

    return "order-status-label";
}

/**
 * 电子面单排序
 * 1、主运单号排在第一个（后台排序）
 * 2、多个运单号按打印时间倒序排列
 */
export const sortEwaybills = (ewaybills?: EwaybillDTO[], mainWaybillNo?: string) => {
    if (ewaybills && ewaybills.length > 1) {
        ewaybills.sort((e1, e2) => {
            if (e1.waybillNo === mainWaybillNo) {
                return -1;
            }
            if (e2.waybillNo === mainWaybillNo) {
                return 1;
            }
            const dateCreated1 = new Date(e1.dateCreated);
            const dateCreated2 = new Date(e2.dateCreated);
            return dateCreated2.getTime() - dateCreated1.getTime();
        });
    }
};

export function buildOrderEwaybillInfos(orders: OrderDTO[]) {
    const summaries = [];

    orders.forEach((order) => {
        const orderEwaybillInfos = [
            order.consignee.localName ?? order.consignee.name,
            order.consignee.localMobile ?? order.consignee.mobile,
        ];

        if (order.ewaybills?.length) {
            order.ewaybills.forEach((ewaybill) => {
                orderEwaybillInfos.push(LogisticsCompanyNameMap[ewaybill.logisticsCompany]);
                orderEwaybillInfos.push(ewaybill.waybillNo);
            });

            const orderText = orderEwaybillInfos.join();

            summaries.push(orderText);
        } else if (order.waybillNo) {
            if (order.logisticsCompany) {
                orderEwaybillInfos.push(LogisticsCompanyNameMap[order.logisticsCompany]);
            }
            orderEwaybillInfos.push(order.waybillNo);

            const orderText = orderEwaybillInfos.join();
            summaries.push(orderText);
        }
    });

    if (summaries.length) {
        const text = summaries.join("\n");

        return text;
    }

    return "";
}

export function buildOrderDiscountInfo(order: Pick<OrderDTO, "discounts" | "adjustAmounts" | "adjustments">) {
    const discountsInfoHtml = [];
    const hasDiscounts = order.discounts && order.discounts > 0;
    if (hasDiscounts) {
        discountsInfoHtml.push("折扣：", order.discounts, "元");
    }

    if (order.adjustments && order.adjustments > 0) {
        if (hasDiscounts) {
            discountsInfoHtml.push("，");
        }

        discountsInfoHtml.push("改价：", order.adjustments, "元");
    }

    if (discountsInfoHtml.length > 0) {
        return discountsInfoHtml.join("");
    }

    return "";
}

/** 示例:
 *  输入:["text^不使用商家标^不使用商家标^"]
 *  输出:不使用商家标
 */
export function getAliFreeEntryServiceInfoContentText(contents: string[]) {
    if (!contents?.length) return "";

    const contextText = contents
        ?.map((item) => {
            if (typeof item !== "string") return "";

            const parts = item.split("^");
            const lastPart = parts[parts.length - 2];

            return lastPart;
        })
        ?.join(";");

    return contextText || "";
}

export function getBigPictureUrl(pictureUrl: string, shopType: ShopType) {
    if (!pictureUrl) return "";

    try {
        // 淘宝 https://img.alicdn.com/bao/uploaded/i3/1609797901/TB2BQPWXBUSMeJjSszeXXcKgpXa_!!1609797901.jpg_60x60.jpg
        if (shopType === ShopType.TAOBAO) {
            if (pictureUrl.indexOf("_60x60.jpg")) {
                return pictureUrl.replace("_60x60.jpg", "");
            }

            if (pictureUrl.indexOf("_300x300.jpg")) {
                return pictureUrl.replace("_300x300.jpg", "");
            }

            return pictureUrl;
        }
        // 阿里巴巴 https://cbu01.alicdn.com/img/order/trading/305/378/524737368031/4853116385_56700090.80x80.jpg
        if (shopType === ShopType.ALIBABA) {
            return pictureUrl.replace(".80x80", "");
        }

        if (shopType === ShopType.TAOBAO_DROP_SHIPPING_SUPPLIER) {
            /** 淘宝供货商代发根据订单来源选择图片规则：淘宝订单使用淘宝图片规则，1688订单使用1688图片规则 */

            if (pictureUrl.startsWith("https://img.alicdn.com")) {
                if (pictureUrl.indexOf("_60x60.jpg")) {
                    return pictureUrl.replace("_60x60.jpg", "");
                }

                if (pictureUrl.indexOf("_300x300.jpg")) {
                    return pictureUrl.replace("_300x300.jpg", "");
                }
            } else if (pictureUrl.startsWith("https://cbu01.alicdn.com")) {
                if (pictureUrl.indexOf(".80x80")) {
                    return pictureUrl.replace(".80x80", "");
                }
            }

            return pictureUrl;
        }

        // 折800 http://z2.tuanimg.com/imagev2/trade/700x700.9406fe2f5b4663eba168e95d83771989.58x58.jpg
        if (shopType === ShopType.ZHE800) {
            return pictureUrl.replace(".58x58", "");
        }
        if (shopType === ShopType.WEIDIAN) {
            return pictureUrl.replace("w=110&h=110&", "");
        }
        return pictureUrl;
    } catch (e) {
        return pictureUrl;
    }
}

/**
 *
 * @see  https://jirr20240624073410550.pingcode.com/pjm/workitems/Lncz1dGz
 */
export function getOrderShippingDeadlineText(shopType: ShopType) {
    if ([ShopType.PINDUODUO, ShopType.PDD_FDS].includes(shopType)) {
        return "最后揽收时间";
    }

    return "最后发货时间";
}

export function buildOrderStatusIcon(status: OrderStatus) {
    if (status === OrderStatus.PENDING_PAYMENT) {
        return <span className="order-status-label label-default">未付款</span>;
    }
    if (status === OrderStatus.PENDING_SHIPMENT) {
        return <span className="order-status-label label-primary">待发货</span>;
    }
    if (status === OrderStatus.SHIPPED) {
        return <span className="order-status-label label-warning">已发货</span>;
    }
    if (status === OrderStatus.SUCCESS) {
        return <span className="order-status-label label-success">已成功</span>;
    }
    if (status === OrderStatus.CANCELED) {
        return <span className="order-status-label label-danger">已取消</span>;
    }

    return null;
}

export function computeSelectAllStatus(orders: OrderDTO[], lockOrderIdMapping: Map<number, any>) {
    const countFn = (accumulator: number, order: OrderDTO) => (order.isSelected ? accumulator + 1 : accumulator);
    const selectedOrdersCount = orders?.reduce(countFn, 0) || 0;

    const lockOrdersCount =
        orders?.reduce((accumulator, order) => {
            const lock = lockOrderIdMapping.get(order.id);

            return lock ? accumulator + 1 : accumulator;
        }, 0) || 0;

    const ordersCount = orders?.length || 0;
    const selectAllCheckboxChecked = selectedOrdersCount === ordersCount - lockOrdersCount;
    const disabled = ordersCount === lockOrdersCount;

    return {
        disabled,
        checked: !disabled && selectAllCheckboxChecked,
    };
}

export function getOrderOrderItems(order: OrderDTO) {
    if (!order) return [];

    if (isMergeOrder(order)) {
        return order.childOrders.flatMap(({ orderItems }) => orderItems);
    }

    return order.orderItems;
}

// 利润= 订单实收金额-订单全部商品的成本价
export function calcOrderProfit(order: OrderDTO) {
    if (!order) return 0;
    if (order.mergeSplitStatus === OrderMergeSplitStatus.SPLIT_CHILD) return 0;

    const orderCost = order.orderItems?.reduce((total, current) => {
        return total + (current.cost || 0);
    }, 0);

    return orderCost && order.sellerReceipts ? Big(order.sellerReceipts).sub(Big(orderCost)).toString() : 0;
}

export {
    isMergerOrder,
    isHistoryOrder,
    buildOrderDetailsUrl,
    buildBuyerRemarks,
    buildSellerRemarks,
    getOriginalIds,
    getConsigneeName,
    getConsigneeMobile,
    getConsigneeTelephone,
    getConsigneeZipCode,
    getConsigneeProvince,
    getConsigneeCity,
    getConsigneeDistrict,
    getConsigneeStreet,
    getConsigneeAddress,
    getOriginalConsigneeAddress,
    getConsigneeInformation,
    sortOrderItemsByStockNo,
    sortOrderItemsByExternalId,
    sortOrderItemsByTitle,
    isAnyOrderItemSelected,
    selectAllAvailableOrderItem,
    shipValidate,
    simplifyProvince,
    hasLocalConsigneeInfo,
    isAbnormalAddress,
    checkUndeliverable,
    isOrderItemShouldBeSelected,
};
